import { Component, computed, inject, OnInit, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    CeButtonComponent,
    CeDropdownImports,
    CeModalImports,
    CeSearchInputImports,
    CeSpinningLoaderComponent,
    DropdownSearchItems,
    DsSearchDropdownComponent,
    OVERLAY_DATA,
    OverlayReference,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { IamUsersApiService } from '@kempoweroyj/ngx-iam-users-api-client';
import {
    IamOrganizationsApiService,
    IamOrganizationsApiServiceObservable,
    ImpersonateV2Request,
} from '@kempoweroyj/ngx-iam-organizations-api-client';
import { CE_SYSTEM_ROLES } from '@kempower-nx-workspace/chargeye-shared';
import { SimplifiedOrganizationDetails } from '../../models/simplified-organization-details.model';
import { ImpersonationService } from '../../data-access/impersonation-service/impersonation.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ImpersonationLocalCacheService } from '../../data-access/impersonation-local-cache/impersonation-local-cache.service';
import { SearchApiServiceObservable } from '@kempoweroyj/ngx-search-api-client';

interface RolesWithNames {
    name: string;
    tenantId?: string;
    description?: string;
    roleId?: string;
}

interface NonSysAdminRoles {
    sysRoles: RolesWithNames[] | [];
    customRoles: RolesWithNames[] | [];
}

@Component({
    selector: 'ce-app-org-switch-dialog',
    providers: [
        SearchApiServiceObservable,
        ImpersonationLocalCacheService,
        IamUsersApiService,
        IamOrganizationsApiService,
        ImpersonationService,
        IamOrganizationsApiServiceObservable,
    ],
    templateUrl: './org-switcher-dialog.component.html',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        CommonModule,
        CeSpinningLoaderComponent,
        CeSearchInputImports,
        CeButtonComponent,
        CeDropdownImports,
        CeModalImports,
        DsSearchDropdownComponent,
    ],
})
export class OrgSwitchDialogComponent implements OnInit {
    /** Inject */
    data = inject(OVERLAY_DATA);
    overlayRef = inject(OverlayReference);
    iamUsersApiService = inject(IamUsersApiService);
    impersonationService = inject(ImpersonationService);
    iamOrganizationsApiService = inject(IamOrganizationsApiService);
    impersonationLocalCache = inject(ImpersonationLocalCacheService);

    /** Form Controls */
    roleControl = new FormControl([]);
    organizationDropdownControl = new FormControl('');

    /** Vars */
    systemRolesArray: Array<[string, string, string]> = Object.entries(CE_SYSTEM_ROLES).map(([key, value]) => [
        key,
        value.label,
        value.description,
    ]);
    searchTerm = '' as string;
    organizations = this.data.organizations ?? [];
    filteredPopoverSearchOptions!: Observable<SimplifiedOrganizationDetails[]>;
    selectedPopoverValue!: string;

    /** Signals */
    userIsSystemAdmin = signal(false);
    impersonationUpdating = signal(false);
    rolesFormControlSignal = toSignal(this.roleControl.valueChanges);
    organizationDropdownControlSignal = toSignal(this.organizationDropdownControl.valueChanges);
    customRolesArray: Signal<Array<[string, string]>> = signal([]);

    ngOnInit() {
        this.userIsSystemAdmin.set(this.data.sysAdmin);
        if (this.data.selectedOrgId) {
            const org = this.organizations.find(
                (org: SimplifiedOrganizationDetails) => org.tenantId === this.data.selectedOrgId
            );
            this.organizationDropdownControl.patchValue(org.tenantName);
            if (this.userIsSystemAdmin()) {
                const currentRoles = this.impersonationLocalCache.getSelectedTenantCachedRoles(org.tenantName);

                if (currentRoles) {
                    const currentRolesCombined = [...currentRoles.roles, ...currentRoles.systemRoles] as never[];
                    if (currentRolesCombined) {
                        this.roleControl.patchValue(currentRolesCombined);
                    }
                }
            }
        }

        this.filteredPopoverSearchOptions = this.organizationDropdownControl.valueChanges.pipe(
            startWith(this.organizationDropdownControl.value),
            map((value: string | null) => this.#filter(value ?? '')),
            map((orgs: SimplifiedOrganizationDetails[]) => {
                if (this.userIsSystemAdmin()) {
                    return orgs;
                }
                orgs.forEach((org: SimplifiedOrganizationDetails) => {
                    if (org.roleDisplayName === undefined) {
                        const roleCount = org.systemRoles.length + (org.rolesWithNames?.length ?? 0);
                        const label = CE_SYSTEM_ROLES[org.systemRoles[0] as keyof typeof CE_SYSTEM_ROLES].label;
                        org.roleDisplayName = roleCount > 1 ? `${roleCount} roles` : (label ?? '');
                    }
                });
                return orgs;
            })
        );
    }

    /**
     * Computed signals
     */
    selectedOrganisation: Signal<SimplifiedOrganizationDetails> = computed(() => {
        const org = this.organizations.find(
            (org: SimplifiedOrganizationDetails) => org.tenantName === this.organizationDropdownControlSignal()
        );
        return org;
    });

    selectedOrganisationName: Signal<string> = computed(() => {
        return this.organizationDropdownControlSignal() ?? '';
    });

    organizationSearchData: Signal<DropdownSearchItems[]> = computed(() => {
        return this.organizations.map((org: SimplifiedOrganizationDetails) => ({
            value: org.tenantName,
        }));
    });

    selectedOrganizationId: Signal<string> = computed(() => {
        if (this.selectedOrganisation() === undefined) {
            return '';
        }
        return this.selectedOrganisation().tenantId;
    });

    roleTriggerText: Signal<{ text: string; selectedRolesTotal: number }> = computed(() => {
        const selectedRoles = this.rolesFormControlSignal();

        if (!selectedRoles || selectedRoles.length === 0) {
            return {
                text: 'Select one or multiple user roles',
                selectedRolesTotal: 0,
            };
        }

        return {
            text: selectedRoles.length === 1 ? selectedRoles[0] : `${selectedRoles.length} roles`,
            selectedRolesTotal: selectedRoles?.length || 0,
        };
    });

    customRolesList: Signal<RolesWithNames[] | []> = computed(() => {
        return this.selectedOrganisation()?.rolesWithNames || [];
    });

    nonSysadminRoles: Signal<NonSysAdminRoles> = computed(() => {
        const nonSysAdminRoles: NonSysAdminRoles = {
            sysRoles: [],
            customRoles: [],
        };

        if (this.userIsSystemAdmin()) {
            return nonSysAdminRoles;
        }

        const org = this.selectedOrganisation();

        if (Array.isArray(org.systemRoles) && org.systemRoles.length > 0) {
            nonSysAdminRoles.sysRoles = org.systemRoles.map((role: string) => {
                const sysRole = CE_SYSTEM_ROLES[role as keyof typeof CE_SYSTEM_ROLES];
                return {
                    name: sysRole.label,
                    description: sysRole.description,
                };
            });
        }
        if (Array.isArray(org.rolesWithNames) && org.rolesWithNames.length > 0) {
            nonSysAdminRoles.customRoles = org.rolesWithNames.map((role) => {
                return role;
            });
        }

        return nonSysAdminRoles;
    });

    /**
     * Helper functions/utils and general methods
     */
    clearRoleSelection() {
        this.roleControl.patchValue([]);
    }

    #filter(value: string): SimplifiedOrganizationDetails[] {
        if (!value) {
            return this.organizations;
        }

        const filterValue = value.toLowerCase();
        return this.organizations.filter((org: SimplifiedOrganizationDetails) =>
            org.tenantName.toLowerCase().includes(filterValue)
        );
    }
    
    cancel() {
        this.overlayRef.close();
    }

    select() {
        let roles: Array<string> = [];
        let systemRoles: Array<string> = [];
        const systemRolesKey: Array<string> = [];

        if (this.userIsSystemAdmin() && this.roleControl?.value != null) {
            const customRolesList = this.customRolesList() as RolesWithNames[];
            for (const roleSelected of this.roleControl.value) {
                const systemRole = this.systemRolesArray.find(([, value]) => value === roleSelected);
                if (systemRole) {
                    systemRoles.push(systemRole[1]);
                    systemRolesKey.push(systemRole[0]);
                }
                const foundRole = customRolesList.find(
                    (customRole: RolesWithNames) => customRole.name === roleSelected
                );

                if (foundRole) {
                    roles.push(roleSelected);
                }
            }
        } else {
            roles = [...this.selectedOrganisation().roles];
            systemRoles = [...this.selectedOrganisation().systemRoles];
        }
        this.organizationDropdownControl.disable();
        this.roleControl.disable();
        this.impersonationUpdating.set(true);

        const dataToSend = {
            tenantId: this.selectedOrganizationId(),
            roles: roles,
            systemRoles: systemRolesKey,
        } as ImpersonateV2Request;

        this.impersonationService.updateImpersonation$(dataToSend).subscribe({
            next: () => {
                this.overlayRef.close({ organizationChanged: true, roles: [...roles, ...systemRoles] });
                if (this.userIsSystemAdmin()) {
                    this.impersonationLocalCache.cacheCurrentOptions(
                        this.selectedOrganizationId(),
                        roles,
                        systemRoles,
                        this.selectedOrganisationName()
                    );
                }
            },
            error: (err) => {
                console.error(err);
            },
        });
    }

    isAllowedSwitchOrg = computed(() => {
        if (this.selectedOrganisation() === undefined) {
            return false;
        }

        const rolesFormControlSignal = this.rolesFormControlSignal();
        const roleSelected = Array.isArray(rolesFormControlSignal) && rolesFormControlSignal?.length >= 1;

        /**
         * An organization must be selected to switch to.
         * If the user is a system admin, they must select a role to switch to that organization.
         * If the user is not a system admin, they don't need to.
         */
        return this.selectedOrganizationId() && (this.userIsSystemAdmin() ? roleSelected : true);
    });
}
